// Copyright 2020 Drivetrain Hub LLC
// General math functions.

// region UNIT CONVERSION

function deg2rad(deg) {
  return Math.PI * deg / 180;
}

function rad2deg(rad) {
  return 180 * rad / Math.PI;
}

function angle2rad(val, units) {
  // Converts degrees to radians only if units are 'deg', otherwise returns input value.
  return units === 'deg' ? deg2rad(val) : val;
}

function angle2deg(val, units) {
  // Converts radians to degrees only if units are 'deg', otherwise returns input value.
  return units === 'deg' ? rad2deg(val) : val;
}

function rpm2radPerSec(rpm) {
  // rev/min to rad/s
  return rpm * Math.PI / 30;
}

function radPerSec2rpm(radPerSec) {
  // rad/s to rev/min
  return radPerSec * 30 / Math.PI;
}

function radPerSec2Hz(radPerSec) {
  // rad/s to cycle/s (Hz)
  return radPerSec / (2 * Math.PI);
}

function mW2kW(milliwatts) {
  // mW to kW
  return milliwatts / 1e6;
}

function kW2mW(kilowatts) {
  // kW to mW
  return kilowatts * 1e6;
}

// endregion

function round(num, digits) {
  // https://stackoverflow.com/questions/2221167/javascript-formatting-a-rounded-number-to-n-decimals
  // Had issues with this approach not rounding certain cases, even though I believe it to be the most math-driven:
  // return +(Math.round(num * Math.pow(10, digits)) * Math.pow(10, -digits));
  return Number(num.toFixed(digits));
}

function nearZero(num, tol=1e-10) {
  return Math.abs(num) < tol;
}

function sum(arr) {
  return arr.reduce((accumulator, val) => (accumulator + val), 0);
}

function mean(arr) {
  return sum(arr) / arr.length;
}

function linspace(startValue, stopValue, num) {
  // https://stackoverflow.com/questions/40475155/does-javascript-have-a-method-that-returns-an-array-of-numbers-based-on-start-s

  let arr = [];
  const step = (stopValue - startValue) / (num - 1);
  for (let i = 0; i < num; i++) {
    arr.push(startValue + (step * i));
  }
  return arr;
}

function logspace(startValue, stopValue) {
  // Returns array spaced according to the order of magnitude, useful for log plots.
  // Ex) 1e2, 2e2, ..., 1e3, 2e3, ..., 1e4, 2e4, ..., etc.

  let arr = [startValue];
  let lastValue = startValue, step;

  while (lastValue < stopValue) {
    step = 10 ** Math.floor(Math.log10(lastValue));
    arr.push(lastValue + step);
    lastValue = arr[arr.length - 1];
  }
  return arr;
}

function matrix(rows, cols, val) {
  // Create an array of arrays with a specified value.

  let mat = [];
  for (let i=0; i < rows; i++) {
    let arr = [];
    for (let j=0; j < cols; j++) {
      arr.push(val);
    }
    mat.push(arr);
  }
  return mat;
}

/**
 * Calculate intersection point(s), if any, of a line and circle.
 * http://www.ambrsoft.com/TrigoCalc/Circles2/circlrLine_.htm
 * @param m - Line slope.
 * @param b - Line y-intercept.
 * @param r - Circle radius.
 * @param xc - Center of circle x-coordinate.
 * @param yc - Center of circle y-coordinate.
 * @return {{x: number, y: number}[]} Two points of intersection.
 */
function lineCircleIntersections(m, b, r, xc, yc) {
  const den = 1 + m**2;
  const k = Math.sqrt(r**2 * den - (yc - m * xc - b)**2);

  const Cx = xc + yc * m - b * m;
  const x1 = (Cx + k) / den;
  const x2 = (Cx - k) / den;

  const Cy = b + xc * m + yc * m**2;
  const y1 = (Cy + m * k) / den;
  const y2 = (Cy - m * k) / den;

  const p1 = {x: x1, y: y1};
  const p2 = {x: x2, y: y2};

  return [p1, p2];
}

function arcBezierCoeffs(p0, p3, xc=0, yc=0) {
  // Create Bezier curve of circular arc.
  // https://stackoverflow.com/questions/734076/how-to-best-approximate-a-geometrical-arc-with-a-bezier-curve

  const ax = p0.x - xc;
  const ay = p0.y - yc;
  const bx = p3.x - xc;
  const by = p3.y - yc;
  const q1 = ax * ax + ay * ay;
  const q2 = q1 + ax * bx + ay * by;
  const k2 = 4/3 * (Math.sqrt(2 * q1 * q2) - q2) / (ax * by - ay * bx);

  const p1 = {
    x: xc + ax - k2 * ay,
    y: yc + ay + k2 * ax,
  };

  const p2 = {
    x: xc + bx + k2 * by,
    y: yc + by - k2 * bx,
  };

  return [p1, p2]
}

function loglogVariables(x0, f0, x1, f1) {
  // Compute the constant and exponent of the log-log function that fits the specified data.

  const exponent = Math.log10(f1 / f0) / Math.log10(x1 / x0);
  const constant = f0 / (x0 ** exponent);

  return [constant, exponent]
}

function loglogFcn(x0, f0, x1, f1) {
  return function (x) {
    // handle edge case with exponent of zero
    const exponent_num = Math.log10(f1 / f0);
    const exponent = exponent_num === 0 ? 0 : exponent_num / Math.log10(x1 / x0);

    return f0 * (x / x0) ** exponent;
  }
}

export default {
  deg2rad: deg2rad,
  rad2deg: rad2deg,
  angle2rad: angle2rad,
  angle2deg: angle2deg,
  rpm2radPerSec: rpm2radPerSec,
  radPerSec2rpm: radPerSec2rpm,
  radPerSec2Hz: radPerSec2Hz,
  mW2kW: mW2kW,
  kW2mW: kW2mW,
  round: round,
  nearZero: nearZero,
  sum: sum,
  mean: mean,
  linspace: linspace,
  logspace: logspace,
  matrix: matrix,
  lineCircleIntersections: lineCircleIntersections,
  arcBezierCoeffs: arcBezierCoeffs,
  loglogVariables: loglogVariables,
  loglogFcn: loglogFcn,
}
